/*
 * 작성자 : 한정문
 * 날짜 : 2023.03.27
 * 기능 : 메뉴 리스트 출력 컴포넌트
 *
 * 작성자 : 홍선영
 * 날짜 : 2023.05.16
 * 수정사항 : 사이드메뉴 리스트 조회 API 연결
 */

import { useState, useEffect, useLayoutEffect } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { useLocation, useNavigate } from 'react-router-dom';

import styled from 'styled-components';
import { IPageInfo, pageInfoState, sideMenuState, userState, asideState, asideFoldState, asideSubState } from '../atoms';
import { IComCdList, ISideMenuObject } from 'customTypes';
import { COMCD_MENU_GRANT } from '../_constants';
import { arraySortByAscdOrder } from '../utils/arraySortByAscdOrder';
import AsideMainMenu from './AsideMainMenu';
import * as cctv from '../utils/cctv';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import { apiGet, apiPost } from '../services/_common';
import SkeletonTable from './SkeletonTable';
import { toast } from 'react-toastify';

const Root = styled.div<IStyleProps>`
  &.hideRoot {
    visibility: hidden;
    position: absolute;
  }
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  display: flex;
  transition: left 0.1s ease-in;
  overflow: visible;
  z-index: 4999;
  @media (min-width: 1025px) {
    position: static;
    height: 100%;
    min-height: 100%;
    bottom: auto;
  }
  .loader-background {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    width: 17.5rem;
    background-color: var(--board);

    .aside-header {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      width: 100%;

      .aside-top {
        width: 100%;

        .aside-top-logo {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 1rem;
          gap: 0.5rem;

          > div {
            padding: 0;

            &:first-child {
              width: auto;
            }
          }
        }
      }

      .aside-menu {
        display: flex;
        width: 100%;
      }
    }

    .aside-bottom {
      width: 100%;
      border-top: 1px solid var(--outline);
    }

    @media (min-width: 640px) {
      width: 16rem;
    }
  }
  .back-filter {
    width: 100%;
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    background-color: black;
    opacity: 0.6;
    z-index: 4999;
    @media (min-width: 1025px) {
      display: none;
    }
  }
`;

interface IStyleProps {
  W: number;
  height: number;
}

interface IAsideProps {
  W: number;
  openMenu: boolean;
}

interface SubObject {
  mtCd: string;
  mdCd: string;
  mdName: string;
  cdSort: number;
}

interface MainObject {
  mtCd: string;
  mtName: string;
  cdSort: number;
  sub: SubObject[];
}

interface IMenuObject {
  mtCd: string;
  menus: MainObject[];
  cdSort?: number;
}

const Aside = ({ W, openMenu }: IAsideProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const resetUserInfo = useResetRecoilState(userState);
  const [userInfo, setUserInfo] = useRecoilState(userState);
  const { userMenuList } = userInfo;
  const pageInfo = useRecoilValue(pageInfoState);
  const setPageInfo = useSetRecoilState<IPageInfo>(pageInfoState);
  const [height, setHeight] = useState(0);
  const [menuGrantComCdList, setMenuGrantComCdList] = useState<IComCdList[]>([]);
  const setOpenAside = useSetRecoilState(asideState);
  const [openSubAside, setOpenSubAside] = useRecoilState(asideSubState);
  const [mainSelected, setMainSelected] = useState<string>('');
  const [mainFocus, setMainFocus] = useState<string>('');
  const [subSelected, setSubSelected] = useState<string>('');
  const [sideMenuList, setSideMenuList] = useRecoilState<ISideMenuObject[]>(sideMenuState);
  const [subMenu, setSubMenu] = useState([]);
  // 데스크톱 환경에서 메뉴 접기/펼치기
  const [loading, setLoading] = useState(false);
  const [foldMenu, setFoldMenu] = useRecoilState<boolean>(asideFoldState);
  const navigate = useNavigate();
  const [useBroadcast, setUseBroadcast] = useState({ isActiveMenu: false, menu: [], pageMoveFunction: () => {} });
  const [hasVisitorMenuAuth, setHasVisitorMenuAuth] = useState(false);
  const [isFontLoaded, setIsFontLoaded] = useState(false); // 폰트 로딩 여부

  const closeAside = () => {
    setOpenAside(false);
  };

  useEffect(() => {
    document.fonts.load('1em "Material Symbols Rounded"').then(() => {
      setIsFontLoaded(true);
    });
  }, []);

  // "위치 관리"(S013/001) 클릭 시 내부 라우팅 대신 LBS로 핸드오프한다 — PMS 세션으로
  // RS256 단기(30초) 토큰을 발급받아 같은 탭에서 LBS 콜백 라우트로 이동시킨다.
  // 토큰 자체엔 로그인 사용자의 hCd/sCd만 담기는데, LBS 관리자 API는 매 요청 hCd/sCd를
  // 요구하므로(SiteScopeGuard) 현재 세션의 hCd/sCd를 쿼리파라미터로 함께 실어 보낸다 —
  // "위치 관리" 메뉴 자체가 현장이 선택된 상태에서만 보이므로 별도 선택 UI는 필요 없다.
  const handleLbsHandoff = async () => {
    try {
      const res = await apiPost({ path: '/pms/internal/lbs-token' });
      const { statusCode, data } = res.data;
      if (statusCode === 200 && data?.token) {
        window.location.href = `${process.env.REACT_APP_LBS_URL}/auth/callback?token=${data.token}&hCd=${userInfo.hCd}&sCd=${userInfo.sCd}`;
      } else {
        toast.error(t('위치 관리로 이동하지 못했습니다.'));
      }
    } catch (error) {
      toast.error(t('위치 관리로 이동하지 못했습니다.'));
    }
  };

  // Aside 메뉴 리스트를 클릭했을 때의 동작을 처리하는 함수
  const onClickAsideList = (el: any) => {
    const { mtCd, mdCd } = el[0];

    // "위치 관리"는 다른 시스템(LBS)으로의 핸드오프라 정상 라우팅 분기보다 먼저 가로챈다
    if (mtCd === 'S013') {
      handleLbsHandoff();
      return;
    }

    // 메뉴가 'home'이 아니고 하위 메뉴가 없는 경우
    if (el !== 'home' && el.length === 1) {
      // 현재 경로와 클릭한 메뉴의 경로가 다를 경우 CCTV 객체 파괴
      if (setLinkPath(mtCd, mdCd) !== pathname) {
        const cctvObject = cctv.getPluginOBJECT()?.oPlugin;
        if (cctvObject) cctv.destroy();
      }

      // 페이지 정보 설정 및 상태 업데이트
      setPageInfoState(`${mtCd}${mdCd}`);
      setSubSelected('');
      setSubMenu([]);
      setMainFocus('');
      setMainSelected(mtCd);
      navigate(`${setLinkPath(el[0].mtCd, el[0].mdCd)}?tabCd=001`);
      setOpenSubAside(false);

      // 메뉴가 'home'인 경우
    } else if (el === 'home') {
      const cctvObject = cctv.getPluginOBJECT()?.oPlugin;
      if (cctvObject) cctv.destroy();

      // 현재 경로가 루트인 경우 0으로 이동, 아닌 경우 루트로 이동
      if (pathname === '/') navigate(0);
      else navigate('/');

      setSubSelected('');
      setSubMenu([]);
      setMainFocus('');
      setMainSelected('');
      setOpenSubAside(false);

      // 그 외의 경우
    } else {
      setSubMenu(el);
      setMainFocus(el[0].mtCd);

      // 메인 포커스가 같은 경우 서브 메뉴 열고 닫기
      if (el[0].mtCd === mainFocus) {
        setMainFocus('');
        setOpenSubAside((prev) => !prev);
      } else {
        setOpenSubAside(true);
      }
    }
  };

  // 페이지 이동 함수
  const pageMovement = (sub: any) => {
    // CCTV 객체 파괴
    const cctvObject = cctv.getPluginOBJECT()?.oPlugin;
    if (cctvObject) {
      cctv.destroy();
    }

    // 포커스 메뉴 찾기
    const focusMenu = sideMenuList.find((list) => list.mtCd === sub.mtCd)?.mtName;
    const { mtCd, mdCd, mdName, sub2 } = sub;

    // 하위 탭 정렬
    const copyArray = [...sub2];
    const sortedTabList = arraySortByAscdOrder(copyArray, 'cdSort');

    // 디폴트 탭 설정
    const defaultTab = sortedTabList?.find((el: any) => el.rDefault === 'Y')?.mrCd || userMenuList?.find((el: any) => el.mtCd === mtCd && el.mdCd === mdCd && el.depth !== '1')?.mrCd; // 디폴트 탭 코드명
    if (focusMenu !== undefined) {
      // 페이지 정보 설정
      setPageInfo((prev) => ({
        ...prev, //
        header: { mtName: focusMenu, mdName },
        defaultMrCd: defaultTab,
        tabList: sortedTabList,
        mtCd: sub.mtCd,
        mdCd: sub.mdCd,
        mrCd: defaultTab,
      }));
    }

    // 메인 및 서브 메뉴 선택 설정
    setMainSelected(mtCd);
    setSubSelected(mdCd);
    // 하위 메뉴 클릭 시 아코디언 유지를 위해 mainFocus 유지
    // setMainFocus('');
    setOpenSubAside(false);
  };

  const foldingMenu = (): void => {
    setFoldMenu(!foldMenu);
    setOpenSubAside(false);
  };

  const viewMainMenu = () => {
    return true;
  };

  useEffect(() => {
    if (openSubAside && cctv.getPluginOBJECT()?.oPlugin) cctv.hideWndAll();
    else if (!openSubAside && cctv.getPluginOBJECT()?.oPlugin) cctv.showWndAll();
  }, [openSubAside]);

  // 사이드메뉴 조회 API
  const getSideMenuListAPI = async () => {
    let req;
    switch (userInfo.gCd.substring(0, 1)) {
      case 'S':
        req = { hCd: userInfo.hCd, sCd: userInfo.sCd };
        break;
      case 'H':
        req = { hCd: userInfo.hCd };
        break;
      case 'A':
        req = { hCd: userInfo.hCd };
        break;
      default:
        break;
    }
    const res = await apiGet({ path: '/menu', req });
    const { statusCode, data, message } = res.data;
    if (statusCode === 200) {
      // 사이드메뉴 조회 데이터 중 mtCd의 첫글자에 따라
      // 마스터/본사/현장/사내 메뉴별로 다시 묶기
      const result: { [key: string]: MainObject[] } = {};
      data.forEach((item: any) => {
        const firstChar = item.mtCd.charAt(0);
        if (!result[firstChar]) {
          result[firstChar] = [];
        }
        result[firstChar].push(item);
      });

      const menuArray: IMenuObject[] = Object.entries(result).map(([key, value]) => ({
        mtCd: key,
        menus: value,
      }));

      // 공통코드 API 조회 해서 메뉴정렬순서를 객체에 추가
      const updatedMenuArray = menuArray.map((menuEl) => {
        const matchingGrant = menuGrantComCdList.find((grant: any) => grant.rootSubCd === menuEl.mtCd);
        if (matchingGrant) {
          return { ...menuEl, cdSort: matchingGrant.cdSort };
        }
        return menuEl;
      });

      const sortedMenuList = arraySortByAscdOrder(updatedMenuArray, 'cdSort');
      const resultArray: any[] = [];
      sortedMenuList.map((el) => {
        const newArray = arraySortByAscdOrder(el.menus, 'cdSort');

        newArray.map((el2) => {
          if (userInfo.gCd === 'MM') {
            if (el2.mtCd.substring(0, 1) === 'M') {
              resultArray.push(el2);
            }
          }
          let chk = false;
          const sub: any[] = [];
          let duplichk = '';
          let el22 = el2;

          userMenuList?.map((v: any) => {
            if (el2.mtCd === v.mtCd) {
              el2.sub.map((el3: any) => {
                if (el3.mdCd === v.mdCd && duplichk !== v.mdCd) {
                  if (userInfo.gCd === 'MM') {
                    if (v.depth === '1') {
                      chk = true;
                      duplichk = v.mdCd;
                      sub.push({ ...el3, useYn: v.useYn });
                    }
                  } else {
                    chk = true;
                    duplichk = v.mdCd;
                    sub.push({ ...el3 });
                  }
                }
              });
              if (userInfo.gCd === 'MM') {
                if (v.depth === '1') {
                  el22 = { ...el2, useYn: v.useYn };
                }
              } else {
                el22 = { ...el2 };
              }
            }
          });
          if (chk) resultArray.push({ ...el22, sub: arraySortByAscdOrder(sub, 'cdSort') });
        });
      });

      // 새로고침시 간혈적으로 마스터메뉴 우선순위가 밀리는 경우 방지
      resultArray.sort((a, b) => {
        // 마스터 메뉴일 경우 우선순위 부여
        if (a.mtCd.substring(0, 1) === 'M' && b.mtCd.substring(0, 1) !== 'M') {
          return -1; // a가 먼저 오도록
        }
        if (a.mtCd.substring(0, 1) !== 'M' && b.mtCd.substring(0, 1) === 'M') {
          return 1; // b가 먼저 오도록
        }

        return a.cdSort - b.cdSort;
      });

      // 유저의 방송 장비 관리 메뉴 권한 여부에 따라 방송하기 퀵버튼 노출 여부 결정
      const hasBroadcastMenu = resultArray.find((el: any) => el.mtCd === 'S011') !== undefined;

      // 방문자 관리 메뉴 권한 여부에 따라 현장선택 셀렉트박스 노출 여부 결정
      const hasVisitorMenu = resultArray.find((el: any) => el.mtCd === 'H006') !== undefined;
      if (hasVisitorMenu) setHasVisitorMenuAuth(true);

      // 방송하기 클릭시 방송장비음원관리 탭으로 이동하기 위한 메뉴값 세팅
      const findSettingMenu = resultArray.find((el: any) => el.mtCd === 'S008');

      if (hasBroadcastMenu) {
        const newSub = findSettingMenu.sub.map((el2: any) => {
          if (el2.mdCd === '010') {
            const newSub2 = el2.sub2.map((el: any) => {
              if (el.mrCd !== '002') return { ...el, rDefault: 'N' };
              return { ...el, rDefault: 'Y' };
            });
            return { ...el2, sub2: newSub2 };
          }
          return el2;
        });

        const findBroadcast = newSub.find((el: any) => el.mtCd === 'S008' && el.mdCd === '010');
        const moveBroadcastPageFunction = () => {
          pageMovement(findBroadcast);
          onClickAsideList([findBroadcast]);
          // 방송하기 버튼 클릭으로 이동시 디폴트탭 강제설정
          setPageInfo((prev) => ({ ...prev, defaultMrCd: '002' }));
        };

        setUseBroadcast({ isActiveMenu: true, menu: newSub, pageMoveFunction: moveBroadcastPageFunction as () => void });
      } else {
        setUseBroadcast({ isActiveMenu: false, menu: [], pageMoveFunction: () => {} });
      }

      setSideMenuList(resultArray);
      setLoading(true);
    } else {
      setLoading(true);
      // toast.error(t(ERROR));
    }
  };

  // 현장 정보 조회 API
  const getSiteInfoAPI = async (hCd: string, sCd: string) => {
    const req = { hCd, sCd };
    const res = await apiGet({ path: '/site/info', req });
    const { data, statusCode, message } = res.data;
    if (statusCode === 200) {
      if (Object.keys(data).length > 0) {
        getSiteSetAPI(hCd, sCd, data.siteInfo);
      }
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 현장 설정 정보 조회 API
  const getSiteSetAPI = async (hCdParam: string, sCdParam: string, siteInfo: any) => {
    /**
     * 수정자 : 한영광
     * 수정일자 : 2024.02.27
     * 수정내용 : 반복된 코드 사용을 줄이고 유지보수 편의성 향상을 위해 리팩토링된 GET 방식 API 요청 함수 적용
     */
    const req = { hCd: hCdParam, sCd: sCdParam };
    const res = await apiGet({ path: '/setting/general', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      if (Object.keys(data).length > 0) {
        setUserInfo((prev) => ({
          ...prev,
          hCd: hCdParam,
          sCd: sCdParam,
          hName: siteInfo.hName,
          sName: siteInfo.sName,
          eExcelYn: siteInfo.eExcelYn,
          eListYn: siteInfo.eListYn,
          ePrintYn: siteInfo.ePrintYn,
          prejobtypeYn: data.generalSet.prejobtypeYn,
          elderlyAgeSet: data.generalSet.elderlyAgeSet,
          sessionTimeout: data.generalSet.sessionTimeout,
          eduPlace: data.generalSet.eduPlace,
          eduCharge: data.generalSet.eduCharge,
          eduStartTime: data.generalSet.eduStartTime,
          eduEndTime: data.generalSet.eduEndTime,
          fromHighpress: data.generalSet.fromHighpress,
          fromLowpress: data.generalSet.fromLowpress,
          toHighpress: data.generalSet.toHighpress,
          toLowpress: data.generalSet.toLowpress,
          accidentEdate: data.generalSet.accidentEdate,
          accidentSdate: data.generalSet.accidentSdate,
          accidentTday: data.generalSet.accidentTday,
          cctvProtocol: data.generalSet.cctvProtocol,
          bScreenMode: data.generalSet.bScreenMode,
          photoSaveYn: data?.generalSet?.photoSaveYn || 'N',
        }));
      }
    } else {
      // toast.error(t(ERROR));
    }
  };

  const isAuthOfMasterUser = userInfo.gCd === 'MM';
  const isAuthOfSiteUser = userInfo.gCd !== 'MM' && userInfo.gCd !== 'AM' && userInfo.gCd !== 'HM' && userInfo.gCd !== 'HU';

  // 본사 메뉴 조회 API
  const getMenuHeadInfoAPI = async (hCdParam: string) => {
    let reqParam;
    if (isAuthOfMasterUser) {
      reqParam = { path: `/menu/headInfo/${hCdParam}` };
    } else {
      reqParam = { path: '/menu/userSideMenu', req: { userId: userInfo.userId } };
    }
    const res = await apiGet(reqParam);
    const { data, statusCode, message } = res.data;
    if (statusCode === 200) {
      setUserInfo((prev) => ({ ...prev, userMenuList: isAuthOfMasterUser ? data : data.userMenuList }));
      if (pathname === '/') {
        setPageInfo((prev) => ({ ...prev, header: { mtName: t('대시보드'), mdName: '' }, mtCd: '', mdCd: '', mrCd: '' }));
        // window.location.replace('/');
      }
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 현장 메뉴 조회 API
  const getMenuSiteInfoAPI = async (hCdParam: string, sCdParam: string) => {
    let reqParam;
    if (isAuthOfSiteUser) {
      reqParam = { path: '/menu/userSideMenu', req: { userId: userInfo.userId } };
    } else {
      reqParam = { path: `/menu/siteInfo/${sCdParam}`, req: { hCd: hCdParam } };
    }
    const res = await apiGet(reqParam);
    const { data, statusCode, message } = res.data;
    if (statusCode === 200) {
      setUserInfo((prev) => ({ ...prev, userMenuList: getUserMenuList(data) }));

      if (pathname === '/') {
        setPageInfo((prev) => ({ ...prev, header: { mtName: t('대시보드'), mdName: '' }, mtCd: '', mdCd: '', mrCd: '' }));
        // window.location.replace('/');
      }
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 사내 메뉴 조회
  const getMenuCompanyInfoAPI = async (cCdParam: string) => {
    let reqParam;
    if (isAuthOfMasterUser) {
      reqParam = { path: `/menu/companyInfo/${cCdParam}` };
    } else {
      reqParam = { path: '/menu/userSideMenu', req: { userId: userInfo.userId } };
    }
    const res = await apiGet(reqParam);
    const { data, statusCode, message } = res.data;
    if (statusCode === 200) {
      setUserInfo((prev) => ({ ...prev, userMenuList: isAuthOfMasterUser ? data : data.userMenuList }));
      if (pathname === '/') {
        setPageInfo((prev) => ({ ...prev, header: { mtName: t('대시보드'), mdName: '' }, mtCd: '', mdCd: '', mrCd: '' }));
      }
    }
  };

  const getUserMenuList = (data: any) => {
    if (isAuthOfMasterUser) {
      return data;
    }
    if (isAuthOfSiteUser) {
      return data.userMenuList;
    }
    return data.filter((v: any) => v.useYn === 'Y');
  };

  const getComCdMenuGrantAPI = async () => {
    const req = { grCd: COMCD_MENU_GRANT };
    const res = await apiGet({ path: '/code/detail', req });
    const { statusCode, data, message } = res.data;
    if (statusCode === 200) {
      setMenuGrantComCdList(data.comCdList);
    } else {
      setLoading(true);
      // toast.error(t(ERROR));
    }
  };

  useEffect(() => {
    getComCdMenuGrantAPI();
  }, []);

  useEffect(() => {
    if (userInfo.userId === 'siteDashboardViewer') {
      resetUserInfo();
      Cookies.remove('prosafeToken');
      Cookies.remove('refreshToken');
    }
  }, [userInfo.userId, pathname]);

  useEffect(() => {
    if (userInfo.hCd !== 'M000') {
      if (userInfo.hCd?.substring(0, 1)?.toLowerCase() === 'c' && userInfo.sCd === '00000') {
        // 사내메뉴 조회
        getMenuCompanyInfoAPI(userInfo.hCd);
      } else if (!userInfo.sCd || userInfo.sCd === '00000') {
        getMenuHeadInfoAPI(userInfo.hCd);
      } else {
        userInfo.gCd !== 'HM' && getMenuSiteInfoAPI(userInfo.hCd, userInfo.sCd);
        getSiteInfoAPI(userInfo.hCd, userInfo.sCd);
      }
    }
  }, [userInfo.hCd, userInfo.sCd]);

  useEffect(() => {
    getSideMenuListAPI();
    if (pathname === '/') {
      setOpenSubAside(false);
      // 대시보드 컴포넌트에서 근로자연명부페이지의 권한을 줌 (근로자 신규등록 및 조회/수정 모달때문)
      setPageInfo((prev) => ({ ...prev, header: { mtName: t('대시보드'), mdName: '' }, mtCd: '', mdCd: '', mrCd: '' }));
    }
  }, [userMenuList]);

  useLayoutEffect(() => {
    sideMenuList.map((sub) => {
      sub.sub?.map((sub2) => {
        if (sub2.mtCd === pageInfo.mtCd) {
          setMainSelected(sub2.mtCd);
        }
      });
    });
  }, []);

  const setPageInfoState = (mtCdmdCd: string) => {
    const mtCd = mtCdmdCd.substring(0, 4);
    const mdCd = mtCdmdCd.substring(4, 7);
    const menuObject: any = sideMenuList.find((el) => el.mtCd === mtCd);

    if (menuObject) {
      const subMenuObject = menuObject.sub.find((el: any) => el.mdCd === mdCd);
      if (subMenuObject) {
        // setMainSelected(mtCd);
        setHeight(menuObject.sub.length * 35);
        const copyArray = [...subMenuObject.sub2];
        const sortedTabList = arraySortByAscdOrder(copyArray, 'cdSort');
        const defaultTab = userMenuList?.find((el: any) => el.mtCd === mtCd && el.mdCd === mdCd && el.depth !== '1')?.mrCd || sortedTabList?.find((el: any) => el.rDefault === 'Y')?.mrCd; // 디폴트 탭 코드명
        // 헤더명, 탭리스트 전역저장
        setPageInfo((prev) => ({
          ...prev,
          header: { mtName: menuObject.mtName, mdName: subMenuObject.mdName },
          defaultMrCd: defaultTab,
          tabList: sortedTabList,
          mtCd,
          mdCd,
          mrCd: defaultTab,
        }));
      }
    }
  };

  useEffect(() => {
    switch (pathname) {
      case '/master1': // 마스터관리-관리자정보
        setPageInfoState('M001001');
        break;
      case '/master2': // 마스터관리-공통코드관리
        setPageInfoState('M001002');
        break;
      case '/master3': // 마스터관리-현장일반코드관리
        setPageInfoState('M001003');
        break;
      case '/master4': // 마스터관리-Dashboard관리
        setPageInfoState('M001004');
        break;
      case '/master6': // 마스터관리-서명항목코드관리
        setPageInfoState('M001006');
        break;
      case '/master7': // 마스터관리-사내양식지항목코드관리
        setPageInfoState('M001007');
        break;
      case '/m_menu1': // 마스터메뉴관리-메뉴관리
        setPageInfoState('M002001');
        break;
      case '/m_menu2': // 마스터메뉴관리-메뉴권한관리
        setPageInfoState('M002002');
        break;
      case '/m_common1': // 일반관리-메뉴관리
        setPageInfoState('M003001');
        break;
      case '/m_common2': // 일반관리-사내관리
        setPageInfoState('M003002');
        break;
      case '/m_common3': // 일반관리-본사관리
        setPageInfoState('M003003');
        break;
      case '/m_common4': // 일반관리-현장관리
        setPageInfoState('M003004');
        break;
      case '/m_common5': // 일반관리-근로자입력화면설정
        setPageInfoState('M003005');
        break;
      case '/m_common6': // 일반관리-현장연결
        setPageInfoState('M003006');
        break;
      case '/m_log': // 일반관리-로그 이력
        setPageInfoState('M003007');
        break;
      case '/m_llm': // 일반관리-로그 이력
        setPageInfoState('M004001');
        break;
      // case 'C001':
      //   '/company1';
      //   break;
      // case 'C002':
      //   '/company2';
      //   break;
      // case 'C003':
      //   '/company3';
      //   break;
      case '/h_info1': // 본사소개-본사정보
        setPageInfoState('H001001');
        break;
      case '/h_user1':
        setPageInfoState('H002001');
        break;
      case '/h_cctv1':
        setPageInfoState('H003001'); // 현장 CCTV-현장 CCTV보기
        break;
      case '/h_cctv2':
        setPageInfoState('H003002'); // 현장 CCTV-CCTV 현황
        break;
      case '/h_sinfo1':
        setPageInfoState('H004001'); // 현장정보/CCTV-현장관리 (본사에서 현장을 확인하는 페이지)
        break;
      case '/h_sinfo2':
        setPageInfoState('H004002'); // 현장정보/CCTV-현장연결 (본사에서 현장을 연결하는 페이지)
        break;
      case '/h_common2': // 본사 설정관리-메인화면관리
        setPageInfoState('H005001');
        break;
      case '/h_log':
        setPageInfoState('H005002'); // 본사 설정관리/로그이력
        break;
      case '/h_common1':
        setPageInfoState('H005003'); // 본사 설정관리/환경설정
        break;
      case '/h_common3':
        setPageInfoState('H005004'); // 본사 설정관리/코드관리
        break;
      case '/h_common4':
        setPageInfoState('H005005'); // 본사 설정관리/CCTV관리
        break;
      case '/h_visitor1':
        setPageInfoState('H006001'); // 본사 방문자 관리
        break;
      case '/h_visitor2':
        setPageInfoState('H006002'); // 본사 방문자 관리
        break;
      case '/s_info1':
        setPageInfoState('S001001'); // 현장 소개 > 현장 정보 / 조감도
        break;
      case '/s_worker1':
        setPageInfoState('S002001'); // 근로자 연명부
        break;
      case '/s_worker2':
        setPageInfoState('S002002'); // 근로자 퇴직처리
        break;
      case '/s_worker4':
        setPageInfoState('S002004'); // 근로자 업체이직
        break;
      case '/s_worker5':
        setPageInfoState('S002005'); // 근로자 보호구지급 관리
        break;
      case '/s_worker6':
        setPageInfoState('S002006'); // 외국인 근로자 관리
        break;
      case '/s_worker7':
        setPageInfoState('S002007'); // 근로자 혈압 관리
        break;
      case '/s_worker8':
        setPageInfoState('S002008'); // 고령 근로자 관리
        break;
      case '/s_worker9':
        setPageInfoState('S002009'); // 근로자 일반건강 관리
        break;
      case '/s_worker10':
        setPageInfoState('S002010'); // 근로자 특수건강 관리
        break;
      case '/s_worker11':
        setPageInfoState('S002011'); // 근로자 현장 출입금지자 관리
        break;
      case '/s_attend1':
        setPageInfoState('S003001'); // 일 출역현황
        break;
      case '/s_attend2':
        setPageInfoState('S003002'); // 월 출역현황
        break;
      case '/s_attend3':
        setPageInfoState('S003003'); // 연 출역현황
        break;
      case '/s_attend4':
        setPageInfoState('S003004'); // 업체별 출역현황
        break;
      case '/s_attend5':
        setPageInfoState('S003005'); // 개인별 일 출역현황
        break;
      case '/s_attend6':
        setPageInfoState('S003006'); // 개인별 월 출역현황
        break;
      case '/s_attend7':
        setPageInfoState('S003007'); // 혈압 이상자 출역현황
        break;
      case '/s_attend8':
        setPageInfoState('S003008'); // 고령 근로자 출역현황
        break;
      case '/s_attend9':
        setPageInfoState('S003009'); // 방문자 현황 조회
        break;
      case '/s_safe1':
        setPageInfoState('S004001'); // 근로자 안전관리
        break;
      case '/s_safe2':
        setPageInfoState('S004002'); // 근로자 안전지적
        break;
      case '/s_safe4':
        setPageInfoState('S004004'); // 안전 관리/게시판
        break;
      case '/s_safe5':
        setPageInfoState('S004005'); // 비인가자 출입 정보
        break;
      case '/s_safe6':
        setPageInfoState('S004006'); // 긴급 상황 현황
        break;
      case '/s_safe7':
        setPageInfoState('S004007'); // AI 카메라
        break;
      case '/s_sensor1':
        setPageInfoState('S005001'); // 미세먼지 측정기
        break;
      case '/s_sensor2':
        setPageInfoState('S005002'); // 소음 측정기
        break;
      case '/s_sensor3':
        setPageInfoState('S005003'); // 질소 측정기
        break;
      case '/s_sensor4':
        setPageInfoState('S005004'); // 풍속 측정기
        break;
      case '/s_sensor5':
        setPageInfoState('S005005'); // 산소 측정기
        break;
      case '/s_sensor6':
        setPageInfoState('S005006'); // 가스 측정기
        break;
      case '/s_sensor7':
        setPageInfoState('S005007'); // 다중 유해가스 측정기
        break;
      case '/s_sensor8':
        setPageInfoState('S005008'); // 무선 유해가스 측정기
        break;
      case '/s_sensor9':
        setPageInfoState('S005009'); // 진동 측정기
        break;
      case '/s_sensor10':
        setPageInfoState('S005010'); // 기울기 측정기
        break;
      case '/s_sensor11':
        setPageInfoState('S005011'); // 균열 측정기
        break;
      case '/s_cctv1':
        setPageInfoState('S006001'); // 현장CCTV-현장 CCTV보기
        break;
      case '/s_user1':
        setPageInfoState('S007001');
        break;
      case '/sm_setting1': // 설정관리-환경설정
        setPageInfoState('S008001');
        break;
      case '/sm_setting2': // 설정관리-근로자 정보 관리
        setPageInfoState('S008002');
        break;
      case '/sm_setting3': // 설정관리-코드관리
        setPageInfoState('S008003');
        break;
      case '/sm_setting4': // 설정관리-메인화면 관리
        setPageInfoState('S008004');
        break;
      case '/sm_setting5': // 설정관리-장비관리
        setPageInfoState('S008005');
        break;
      case '/sm_setting6': // 설정관리-협력업체관리
        setPageInfoState('S008006');
        break;
      case '/sm_setting7': // 설정관리-CCTV관리
        setPageInfoState('S008007');
        break;
      case '/s_log': // 설정관리-로그이력
        setPageInfoState('S008008');
        break;
      case '/s_tunnel1': // 터널관리-구역 현황 관리
        setPageInfoState('S010001');
        break;
      case '/s_safeedu1': // 태블릿 근로자관리-근로자 관리
        setPageInfoState('S012001');
        break;
      case '/s_safeedu2': // 태블릿 근로자관리-안전교육 일정 관리
        setPageInfoState('S012002');
        break;
      case '/sm_setting9': // 설정관리-터널 설정 관리
        setPageInfoState('S008009');
        break;
      case '/sm_setting10': // 설정관리-방송 장비 관리
        setPageInfoState('S008010');
        break;
      case '/sm_setting11':
        setPageInfoState('S008011'); // 알림톡 관리
        break;
      case '/sm_setting12':
        setPageInfoState('S008012'); // 태블릿 안전교육 관리
        break;
      case '/sm_setting13':
        setPageInfoState('S008013'); // 방문자 설정 관리
        break;
      case '/s_broadcast':
        setPageInfoState('S011001'); // 방송 장비 관리
        break;
      case '/c_work1':
        setPageInfoState('C001001'); // 업무관리-일정관리
        break;
      case '/c_work2':
        setPageInfoState('C001002'); // 업무관리-출장관리
        break;
      case '/c_work3':
        setPageInfoState('C001003'); // 업무관리-보고서관리
        break;
      case '/c_work4':
        setPageInfoState('C001004'); // 업무관리-결재관리
        break;
      case '/c_work5':
        setPageInfoState('C001005'); // 업무관리-근태관리
        break;
      case '/c_work6':
        setPageInfoState('C001006'); // 업무관리-개인메모
        break;
      case '/c_work7':
        setPageInfoState('C001007'); // 업무관리-기타관리
        break;
      case '/c_company1':
        setPageInfoState('C002001'); // 사내관리-사원정보
        break;
      case '/c_company2':
        setPageInfoState('C002002'); // 사내관리-안전관리/게시판관리
        break;
      case '/c_company3':
        setPageInfoState('C002003'); // 사내관리-우수사원
        break;
      case '/c_attend1':
        setPageInfoState('C003001'); // (사내) 출역관리-일 출역현황
        break;
      case '/c_attend2':
        setPageInfoState('C003002'); // (사내) 출역관리-월 출역현황
        break;
      case '/c_attend3':
        setPageInfoState('C003003'); // (사내) 출역관리-연 출역현황
        break;
      case '/c_attend4':
        setPageInfoState('C003004'); // (사내) 출역관리-개인별 일 출역현황
        break;
      case '/c_attend5':
        setPageInfoState('C003005'); // (사내) 출역관리-개인별 월 출역현황
        break;
      case '/c_setting1':
        setPageInfoState('C005001'); // 설정관리-환경설정
        break;
      case '/c_setting2':
        setPageInfoState('C005002'); // 설정관리-코드관리
        break;
      case '/c_setting3':
        setPageInfoState('C005003'); // 설정관리-메인화면 관리
        break;
      case '/c_setting4':
        setPageInfoState('C005004'); // 설정관리-장비 관리
        break;
      case '/c_setting5':
        setPageInfoState('C005005'); // 설정관리-고객사 관리
        break;
      case '/c_setting6':
        setPageInfoState('C005006'); // 설정관리-일정 관리
        break;
      case '/c_setting7':
        setPageInfoState('C005007'); // 설정관리-양식지 관리
        break;
      case '/c_setting11':
        setPageInfoState('C005011'); // 설정관리-근로자 입력화면 관리
        break;
      case '/c_log':
        setPageInfoState('C005008'); // 설정관리-로그 이력
        break;
      case '/c_user1':
        setPageInfoState('C006001'); // 설정관리-사원정보
        break;
      case '/c_board':
        setPageInfoState('C007001'); // 자유게시판
        break;
      default:
        break;
    }
  }, [pathname]);

  const setLinkPath = (mtCd: string, mdCd: string) => {
    const code = `${mtCd}${mdCd}`;
    switch (code) {
      case 'M001001': // 마스터관리-관리자정보
        return '/master1';
      case 'M001002': // 마스터관리-공통코드관리
        return '/master2';
      case 'M001003': // 마스터관리-현장일반코드관리
        return '/master3';
      case 'M001004': // 마스터관리-Dashboard관리
        return '/master4';
      case 'M001006': // 마스터관리-서명항목코드관리
        return '/master6';
      case 'M001007': // 마스터관리-사내양식지항목코드관리
        return '/master7';
      case 'M002001': // 마스터메뉴관리-메뉴관리
        return '/m_menu1';
      case 'M002002': // 마스터메뉴관리-메뉴권한관리
        return '/m_menu2';
      case 'M003001': // 일반관리-메뉴관리
        return '/m_common1';
      case 'M003002': // 일반관리-사내관리
        return '/m_common2';
      case 'M003003': // 일반관리-본사관리
        return '/m_common3';
      case 'M003004': // 일반관리-현장관리
        return '/m_common4';
      case 'M003005': // 일반관리-근로자입력화면설정
        return '/m_common5';
      case 'M003006': // 일반관리-현장연결
        return '/m_common6';
      case 'M003007': // 일반관리-로그이력
        return '/m_log';
      case 'M004001': // 일반관리-LLM 데이터관리
        return '/m_llm';
      case 'C001':
        return '/company1';
      case 'C002':
        return '/company2';
      case 'C003':
        return '/company3';
      case 'H001001': // 본사소개-본사정보
        return '/h_info1';
      case 'H002001':
        return '/h_user1';
      case 'H003001':
        return '/h_cctv1'; // 현장 CCTV-현장 CCTV 보기
      case 'H003002':
        return '/h_cctv2'; // 현장 CCTV-CCTV 현황
      case 'H004001':
        return '/h_sinfo1'; // 현장정보/CCTV-현장관리 (본사에서 현장을 확인하는 페이지)
      case 'H004002':
        return '/h_sinfo2'; // 현장정보/CCTV-현장연결 (본사에서 현장을 연결하는 페이지)
      case 'H005001':
        return '/h_common2'; // 본사 설정관리 - 메인화면관리
      case 'H005002':
        return '/h_log'; // 본사 설정관리 - 로그이력
      case 'H005003':
        return '/h_common1'; // 본사 설정관리 - 환경설정
      case 'H005004':
        return '/h_common3'; // 본사 설정관리 - 코드관리
      case 'H005005':
        return '/h_common4'; // 본사 설정관리 - CCTV관리
      case 'H006001':
        return '/h_visitor1'; // 본사 방문자 관리
      case 'H006002':
        return '/h_visitor2'; // 본사 방문자 관리
      case 'S001001':
        return '/s_info1'; // 현장 소개 > 현장 정보 / 조감도
      case 'S002001':
        return '/s_worker1'; // 근로자 연명부
      case 'S002002':
        return '/s_worker2'; // 근로자 퇴직처리
      case 'S002004':
        return '/s_worker4'; // 근로자 업체이직
      case 'S002005':
        return '/s_worker5'; // 근로자 보호구지급 관리
      case 'S002006':
        return '/s_worker6'; // 외국인 근로자 관리
      case 'S002007':
        return '/s_worker7'; // 근로자 혈압 관리
      case 'S002008':
        return '/s_worker8'; // 고령 근로자 관리
      case 'S002009':
        return '/s_worker9'; // 근로자 일반건강 관리
      case 'S002010':
        return '/s_worker10'; // 근로자 특수건강 관리
      case 'S002011':
        return '/s_worker11'; // 근로자 현장 출입금지자 관리
      case 'S003001':
        return '/s_attend1'; // 일 출역현황
      case 'S003002':
        return '/s_attend2'; // 월 출역현황
      case 'S003003':
        return '/s_attend3'; // 연 출역현황
      case 'S003004':
        return '/s_attend4'; // 업체별 출역현황
      case 'S003005':
        return '/s_attend5'; // 개인별 일 출역현황
      case 'S003006':
        return '/s_attend6'; // 개인별 월 출역현황
      case 'S003007':
        return '/s_attend7'; // 혈압 이상자 출역현황
      case 'S003008':
        return '/s_attend8'; // 고령 근로자 출역현황
      case 'S003009':
        return '/s_attend9'; // 방문자 현황 조회
      case 'S004001':
        return '/s_safe1'; // 근로자 안전관리
      case 'S004002':
        return '/s_safe2'; // 근로자 안전지적
      // case 'S004003':
      //   return '/s_safe3'; // 근로자 안전지적(근로자)
      case 'S004004':
        return '/s_safe4'; // 안전 관리/게시판
      case 'S004005':
        return '/s_safe5'; // 비인가자 출입 정보
      case 'S004006':
        return '/s_safe6'; // 긴급 상황 현황
      case 'S004007':
        return '/s_safe7'; // AI 카메라
      case 'S005001':
        return '/s_sensor1'; // 미세먼지 측정기
      case 'S005002':
        return '/s_sensor2'; // 소음 측정기
      case 'S005003':
        return '/s_sensor3'; // 질소 측정기
      case 'S005004':
        return '/s_sensor4'; // 풍속 측정기
      case 'S005005':
        return '/s_sensor5'; // 산소 측정기
      case 'S005006':
        return '/s_sensor6'; // 가스 측정기
      case 'S005007':
        return '/s_sensor7'; // 다중 유해가스 측정기
      case 'S005008':
        return '/s_sensor8'; // 무선 유해가스 측정기
      case 'S005009':
        return '/s_sensor9'; // 진동 측정기
      case 'S005010':
        return '/s_sensor10'; // 기울기 측정기
      case 'S005011':
        return '/s_sensor11'; // 균열 측정기
      case 'S006001':
        return '/s_cctv1'; // 현장CCTV-현장 CCTV보기
      case 'S007001':
        return '/s_user1';
      case 'S008001': // 설정관리-환경설정
        return '/sm_setting1';
      case 'S008002': // 설정관리-근로자 정보 관리
        return '/sm_setting2';
      case 'S008003': // 설정관리-코드관리
        return '/sm_setting3';
      case 'S008004': // 설정관리-메인화면 관리
        return '/sm_setting4';
      case 'S008005': // 설정관리-장비관리
        return '/sm_setting5';
      case 'S008006': // 설정관리-협력업체관리
        return '/sm_setting6';
      case 'S008007': // 설정관리-CCTV관리
        return '/sm_setting7';
      case 'S008008': // 설정관리-로그이력
        return '/s_log';
      case 'S010001': // 터널관리-구역 현황 관리
        return '/s_tunnel1';
      case 'S012001': // 태블릿 근로자관리-근로자 관리
        return '/s_safeedu1';
      case 'S012002': // 태블릿 근로자관리-안전교육 일정 관리
        return '/s_safeedu2';
      case 'S008009': // 설정관리-터널관리
        return '/sm_setting9';
      case 'S008010': // 설정관리-방송 장비 관리
        return '/sm_setting10';
      case 'S008011': // 설정관리-알림톡 관리
        return '/sm_setting11';
      case 'S008012': // 설정관리-태블릿 안전교육 관리
        return '/sm_setting12';
      case 'S008013': // 설정관리-방문자 관리
        return '/sm_setting13';
      case 'S011001':
        return '/s_broadcast'; // 방송 장비 관리
      case 'C001001': // 업무관리-일정관리
        return '/c_work1';
      case 'C001002': // 업무관리-출장관리
        return '/c_work2';
      case 'C001003': // 업무관리-보고서관리
        return '/c_work3';
      case 'C001004': // 업무관리-결재관리
        return '/c_work4';
      case 'C001005': // 업무관리-근태관리
        return '/c_work5';
      case 'C001006': // 업무관리-개인메모
        return '/c_work6';
      case 'C001007': // 업무관리-기타관리
        return '/c_work7';
      case 'C002001':
        return '/c_company1'; // 사내관리-사원정보
      case 'C002002':
        return '/c_company2'; // 사내관리-안전관리/게시판관리
      case 'C002003':
        return '/c_company3'; // 사내관리-우수사원
      case 'C003001':
        return '/c_attend1'; // (사내) 출역관리-일 출역현황
      case 'C003002':
        return '/c_attend2'; // (사내) 출역관리-월 출역현황
      case 'C003003':
        return '/c_attend3'; // (사내) 출역관리-연 출역현황
      case 'C003004':
        return '/c_attend4'; // (사내) 출역관리-개인별 일 출역현황
      case 'C003005':
        return '/c_attend5'; // (사내) 출역관리-개인별 월 출역현황
      case 'C005001':
        return '/c_setting1'; // 설정관리-환경설정
      case 'C005002':
        return '/c_setting2'; // 설정관리-코드관리
      case 'C005003':
        return '/c_setting3'; // 설정관리-메인화면 관리
      case 'C005004':
        return '/c_setting4'; // 설정관리-장비 관리
      case 'C005005':
        return '/c_setting5'; // 설정관리-고객사 관리
      case 'C005006':
        return '/c_setting6'; // 설정관리-일정 관리
      case 'C005007':
        return '/c_setting7'; // 설정관리-양식지 관리
      case 'C005011':
        return '/c_setting11'; // 설정관리-근로자 입력화면 관리
      case 'C005008':
        return '/c_log'; // 설정관리-로그 이력
      case 'C006001':
        return '/c_user1'; // 설정관리-사원정보
      case 'C007001':
        return '/c_board'; // 자유게시판
      default:
        return '/';
    }
  };

  if (!loading || !isFontLoaded) {
    return (
      <Root W={W} height={height} className={`loading-aside ${openMenu ? 'showRoot' : 'hideRoot'}`}>
        <div className='loader-background'>
          <div className='aside-header'>
            <div className='aside-top'>
              <div className='aside-top-logo'>
                <SkeletonTable rows={0} mainRowWidth='64px' mainRowHeight={64} />
                <SkeletonTable rows={0} mainRowWidth='100%' mainRowHeight={40} rowHeight={40} />
              </div>
            </div>
            <div className='aside-menu'>
              <SkeletonTable rows={9} mainRowHeight={36} rowHeight={36} />
            </div>
          </div>
          <div className='aside-bottom'>
            <SkeletonTable rows={0} mainRowHeight={40} rowHeight={40} />
          </div>
        </div>
      </Root>
    );
  }

  return (
    <Root W={W} height={height} className={openMenu ? 'showRoot' : 'hideRoot'}>
      <AsideMainMenu
        foldMenu={foldMenu}
        foldingMenu={foldingMenu}
        sideMenuList={sideMenuList}
        onClickAsideList={onClickAsideList}
        mainSelected={mainSelected}
        mainFocus={mainFocus}
        closeAside={closeAside}
        openMenu={viewMainMenu()}
        useBroadcast={useBroadcast}
        hasVisitorMenuAuth={hasVisitorMenuAuth}
        W={W}
        subMenu={subMenu}
        pageMovement={pageMovement}
        setLinkPath={setLinkPath}
        subSelected={subSelected}
        setSubSelected={setSubSelected}
        setMainFocus={setMainFocus}
      />
      <div className='back-filter' onClick={() => closeAside()} role='presentation' />
    </Root>
  );
};
export default Aside;
